import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, type AuthUser } from './api.js';

/**
 * Resultado do primeiro passo do login: ou já autenticou (2FA off) ou o
 * caller precisa completar via `verifyLoginTwoFactor(code)` com o token
 * devolvido aqui.
 */
export type LoginStep =
  | { kind: 'authenticated' }
  | { kind: 'requires_2fa'; challengeToken: string };

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  /** Primeiro passo do login: senha. Devolve o estado seguinte. */
  login: (email: string, password: string) => Promise<LoginStep>;
  /** Segundo passo do login quando o usuário tem 2FA ativado. */
  verifyLoginTwoFactor: (challengeToken: string, code: string) => Promise<void>;
  loginWithSso: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then(({ user }) => setUser(user))
      .catch((e) => {
        if (!(e instanceof ApiError) || e.status !== 401) console.error(e);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string): Promise<LoginStep> => {
    const res = await api.login(email, password);
    if ('requires2fa' in res && res.requires2fa) {
      return { kind: 'requires_2fa', challengeToken: res.challengeToken };
    }
    setUser(res.user);
    return { kind: 'authenticated' };
  };

  const verifyLoginTwoFactor = async (challengeToken: string, code: string) => {
    const { user } = await api.verifyLoginTwoFactor(challengeToken, code);
    setUser(user);
  };

  const loginWithSso = async (token: string) => {
    const { user } = await api.sso(token);
    setUser(user);
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  };

  const refresh = async () => {
    try {
      const { user } = await api.me();
      setUser(user);
    } catch {
      setUser(null);
    }
  };

  return (
    <AuthCtx.Provider
      value={{ user, loading, login, verifyLoginTwoFactor, loginWithSso, logout, refresh }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth precisa de <AuthProvider>');
  return v;
}
